import { Service, inject, computed, effect, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionsStore } from './permissions.store';
import { ProjectContextStore } from '@core/project-context/project-context.store';
import { ResourcePermissionsEnum, TypeProjectCrew } from './permissions.enum';
import { SegmentResourceEnum } from './permissions.enum';
import { ResourcePermissionType } from './models/resource-permission.model';

@Service()
export class PermissionsService {
  private router = inject(Router);
  private permissionsStore = inject(PermissionsStore);
  private projectContextStore = inject(ProjectContextStore);
  readonly isSuperAdmin = computed(() => this.permissionsStore.isSuperAdmin());
  readonly isProjectViewer = computed(() => this.permissionsStore.typeProjectCrew() === TypeProjectCrew.Team21MTCrewProject);
  readonly isEngineerCrew = computed(() => this.permissionsStore.typeProjectCrew() === TypeProjectCrew.EnginnerCrewProject);

  /**
   * The project the last pass ran against. Unset until the first permissions
   * arrive, so hydrating a fresh session establishes the baseline instead of
   * re-navigating — see `revalidateCurrentRoute()`.
   */
  private validatedProjectId: number | null = null;

  constructor() {
    effect(() => {
      // Read purely as a trigger: the store rewrites both project slices when
      // the active project changes, so this fires once the new project's
      // permissions are in place. Which of the two slices is read does not
      // matter — Angular batches effects, so this one runs after the store's
      // effect has finished writing both.
      this.permissionsStore.selectedProjectDataGroupPermissions();

      // Untracked on purpose: this is the *subject* of the check, not its
      // trigger. Depending on it would let this effect run on the project
      // change itself, ahead of the store rewriting the slices above, and
      // re-validate against the outgoing project's permissions.
      const projectId = untracked(() => this.projectContextStore.activeProjectId());
      const switchedProject =
        this.validatedProjectId !== null && projectId !== this.validatedProjectId;
      this.validatedProjectId = projectId;

      if (switchedProject && !this.isSuperAdmin()) {
        this.revalidateCurrentRoute();
      }
    });
  }

  hasResourcePermission(permission: ResourcePermissionsEnum, type: ResourcePermissionType): boolean {
    if (this.isSuperAdmin()) return true;

    const resourcePermission = this.permissionsStore.selectedProjectResourcePermissions().find(p => p.idResource === permission);
    if (!resourcePermission) {
      return false;
    }

    return resourcePermission[type] ?? false;
  }

  hasDataGroupPermission(segment: SegmentResourceEnum, listDataItem: string): boolean {
    if (this.isSuperAdmin()) return true;

    const dataGroupPermission = this.permissionsStore.selectedProjectDataGroupPermissions().find(p => p.segment === segment);
    if (!dataGroupPermission) return false;

    const dataItem = dataGroupPermission.listData.find(item => item.name === listDataItem);
    return dataItem ? dataItem.isActive : false;
  }

  /**
   * Re-runs the route guards for the page the user is already on, so someone who
   * switches to a project that no longer grants that page is moved off it rather
   * than left sitting on a screen they may not see. Three pieces are needed and
   * all three are load-bearing: this navigation, `onSameUrlNavigation: 'reload'`
   * in `app.config.ts`, and `runGuardsAndResolvers: 'always'` on each guarded
   * route — without the last two the router treats a same-URL navigation as a
   * no-op and never re-runs `canActivate`.
   *
   * Only a genuine *switch* reaches here. The first permissions of a session
   * must not, because they arrive while the router is still on its way off
   * `/login`: this navigation would target `/login` — the URL being left — and
   * supersede the post-login one, stranding the user on the login page with a
   * valid session. It also kept the page-you-were-on restore from ever landing.
   * The same gate removes the several redundant same-URL navigations each login
   * used to issue.
   */
  private revalidateCurrentRoute() {
    if (!this.router.navigated) return;

    this.router.navigateByUrl(this.router.url, { replaceUrl: true });
  }
}
